import React, { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { 
  Typography, 
  Box, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  DialogActions,
  Button
} from "@mui/material";
import { List as ListIcon, Close as CloseIcon } from "@mui/icons-material";
import { formatCurrency, getCurrencySymbol } from "../utils/currencyFormatter";

const RadialChart = ({ data, title, showLegend = true, userCurrency }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!data || data.length === 0) return <p>No data available</p>;

  const rawData = data.map((item) => ({
    name: item.category || item.vendor,
    value: Math.round(parseFloat(item.total) * 100) / 100
  }));

  const totalAmount = rawData.reduce((sum, item) => sum + item.value, 0);
  
  const threshold = 0.01; // 1%
  const significantItems = [];
  let otherTotal = 0;
  let otherCount = 0;

  rawData.forEach(item => {
    const percentage = item.value / totalAmount;
    if (percentage >= threshold) {
      significantItems.push(item);
    } else {
      otherTotal += item.value;
      otherCount++;
    }
  });

  const chartData = [...significantItems];
  if (otherTotal > 0) {
    chartData.push({
      name: `Other (${otherCount} items)`,
      value: Math.round(otherTotal * 100) / 100,
      isOther: true
    });
  }

  chartData.sort((a, b) => {
    if (a.isOther) return 1;
    if (b.isOther) return -1;
    return b.value - a.value;
  });

  const COLORS = [
    "#FF6B8A", "#4ECDC4", "#45B7D1", "#96CEB4", "#FECCA7", 
    "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9"
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const value = data.value;
      const percentage = ((value / totalAmount) * 100).toFixed(2);
      
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.98)', 
          padding: '12px 16px', 
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          fontWeight: '500',
          backdropFilter: 'blur(8px)'
        }}>
          <p style={{ margin: 0, color: '#333' }}>
            {data.payload.isOther 
              ? `${data.name}: ${formatCurrency(value, userCurrency)} (${percentage}%)`
              : `${data.name}: ${formatCurrency(value, userCurrency)} (${percentage}%)`
            }
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, payload }) => {
    // Only show labels for slices larger than 5% to avoid overcrowding
    if (percent < 0.05) return null;
    
    const RADIAN = Math.PI / 180;
    // Position labels further outside the pie
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Show shorter name for "Other" category
    const displayName = payload.isOther ? 'Other' : name;

    return (
      <text 
        x={x} 
        y={y} 
        fill="#333" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
        style={{ 
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
          pointerEvents: 'none' 
        }}
      >
        {displayName}
      </text>
    );
  };

  return (
    <Box textAlign="center" sx={{ position: 'relative' }}>
      {/* List Button */}
      <IconButton
        onClick={() => setIsDialogOpen(true)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          },
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <ListIcon />
      </IconButton>

      <Typography 
        variant="h6" 
        gutterBottom
        sx={{ 
          fontWeight: 600,
          color: '#2c3e50',
          mb: 3
        }}
      >
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={380}>
        <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={120}
            innerRadius={0}
            fill="#8884d8"
            dataKey="value"
            stroke="#fff"
            strokeWidth={2}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend 
              verticalAlign="bottom" 
              height={50}
              wrapperStyle={{ 
                fontSize: '13px',
                paddingTop: '15px'
              }}
              iconType="circle"
            />
          )}
        </PieChart>
      </ResponsiveContainer>

      {/* Detail Dialog */}
      <Dialog 
        open={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 600
        }}>
          {title} - Detailed Breakdown
          <IconButton onClick={() => setIsDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {title.includes('Category') ? 'Category' : 'Vendor'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total ({getCurrencySymbol(userCurrency)})</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rawData
                  .sort((a, b) => b.value - a.value) // Sort by total descending
                  .map((item, index) => {
                    const percentage = ((item.value / totalAmount) * 100).toFixed(2);
                    return (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:hover': { backgroundColor: '#f8fafc' },
                          '&:nth-of-type(odd)': { backgroundColor: '#fdfdfd' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {item.value.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          fontWeight: 600,
                          color: percentage >= 10 ? '#2563eb' : percentage >= 5 ? '#059669' : '#64748b'
                        }}>
                          {percentage}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Summary */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              📊 Summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                • <strong>Total Items:</strong> {rawData.length}
              </Typography>
              <Typography variant="body2">
                • <strong>Total Amount:</strong> {totalAmount.toFixed(2)}
              </Typography>
              <Typography variant="body2">
                • <strong>Largest Item:</strong> {rawData[0]?.name} ({((rawData[0]?.value / totalAmount) * 100).toFixed(1)}%)
              </Typography>
              <Typography variant="body2">
                • <strong>Items &gt; 10%:</strong> {rawData.filter(item => (item.value / totalAmount) > 0.1).length}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RadialChart;